/** 设计系统：少量组件，全部只引用晨昏令牌。按钮遵循“主 1 + 次 2”。 */
import clsx from "clsx";
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from "react";

type Variant = "primary" | "secondary" | "ghost" | "signal" | "danger";

export function Button({
  variant = "secondary",
  size = "md",
  busy,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md"; busy?: boolean }) {
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-s font-semibold transition-colors duration-[var(--motion-fast)] disabled:cursor-not-allowed disabled:opacity-50",
        size === "md" ? "h-8 px-3.5 text-[13px]" : "h-7 px-2.5 text-[12px]",
        variant === "primary" && "bg-action text-action-text hover:opacity-90",
        variant === "secondary" && "border border-border-strong bg-surface text-text hover:bg-surface-2",
        variant === "ghost" && "text-text-2 hover:bg-surface-2 hover:text-text",
        variant === "signal" && "bg-signal-ink text-on-signal hover:opacity-90",
        variant === "danger" && "border border-err text-err hover:bg-err-soft",
        className,
      )}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={clsx(
        "inline-block size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent",
        className,
      )}
    />
  );
}

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={clsx("rounded-m border border-border bg-surface shadow-soft", className)} />
  );
}

export function Pill({
  tone = "neutral",
  className,
  children,
}: {
  tone?: "neutral" | "signal" | "ok" | "warn" | "err";
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        "inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[12px]",
        tone === "neutral" && "bg-surface-2 text-text-2",
        tone === "signal" && "bg-signal-soft text-signal-ink",
        tone === "ok" && "bg-ok-soft text-ok",
        tone === "warn" && "bg-warn-soft text-warn",
        tone === "err" && "bg-err-soft text-err",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function SignalDot() {
  return (
    <span aria-hidden className="size-1.5 rounded-full bg-signal shadow-[0_0_0_3px_var(--signal-soft)]" />
  );
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-m border border-dashed border-border-strong p-6 text-text-2">
      <div className="font-semibold text-text">{title}</div>
      {children ? <div className="text-[13px] leading-relaxed">{children}</div> : null}
      {action}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="rounded-s border border-err/40 bg-err-soft px-3 py-2 text-[13px] text-err">
      {children}
    </div>
  );
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={clsx(
        "h-8 w-full rounded-s border border-border-strong bg-surface px-2.5 text-[13px] text-text placeholder:text-text-3",
        className,
      )}
    />
  );
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      className={clsx(
        "w-full rounded-s border border-border-strong bg-surface px-2.5 py-2 text-[13px] leading-relaxed text-text placeholder:text-text-3",
        className,
      )}
    />
  );
}

export function PageHeader({
  title,
  crumb,
  children,
}: {
  title: string;
  crumb?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end gap-3 px-4 md:px-7 pt-5 pb-3">
      <div className="min-w-0">
        {crumb ? <div className="mb-1 text-[12px] text-text-3">{crumb}</div> : null}
        <h1 className="truncate text-[20px] font-bold tracking-wide">{title}</h1>
      </div>
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </header>
  );
}

export const STAGE_TEXT: Record<string, string> = {
  untested: "未测",
  attempted: "已尝试",
  familiar: "熟悉",
  proficient: "熟练",
  mastered: "已掌握",
  consolidating: "待巩固",
};

export const VERDICT_TEXT: Record<string, string> = {
  correct: "答对了",
  partial: "部分正确",
  incorrect: "没答对",
  uncertain: "暂时无法判定",
};
